// src/utils/couleursMetier.ts — SCE Issues
// Couleurs métier (Lot 8, demande 12) : ce qui est stocké, ce qui s'applique,
// qui peut changer quoi. Module pur — aucun réseau, aucun React.
//
// ─── Décisions du cadrage appliquées ───
//
//  9-A  Réglage GLOBAL : la couleur d'un métier est un code de lecture
//       partagé. Une préférence individuelle ferait mentir les captures
//       d'écran collées dans Teams.
//  10-C Stocké dans SCE_UserPrefs, ligne `Title = '__global__'` (cf.
//       couleursMetier.api.ts).
//  11-A Le coordinateur du métier change SA couleur ; l'administrateur toutes.
//       Une teinte déjà prise par un autre métier est refusée.
//
// ─── Forme stockée ───
//
//   { version: 1, couleursMetier: { MECA: { hex, par, le }, … } }
//
// Seuls les métiers CHANGÉS y figurent ; les autres gardent leur couleur
// d'origine. `par` / `le` disent qui a changé et quand : il n'y a pas de
// timeline pour un réglage, c'est la seule trace.

import type { Metier } from '@/types/issue.types'
import { METIERS_ORDRE } from '@/config/metiers.config'
import { estAdmin } from '@/config/admin.config'
import { getMetiersCoordonnes } from '@/config/coordinateurs.config'
import {
  COULEURS_METIER_DEFAUT,
  PALETTE_METIER,
  teinteParHex,
  texteSur,
} from '@/config/palette.config'

export const VERSION_REGLAGE = 1

export interface ChoixCouleur {
  /** Hex de la palette, majuscules. */
  hex: string
  /** Nom de qui l'a choisie (compte réel). */
  par: string
  /** ISO 8601. */
  le: string
}

export interface ReglageCouleurs {
  metiers: Partial<Record<Metier, ChoixCouleur>>
}

export const REGLAGE_VIDE: ReglageCouleurs = { metiers: {} }

/**
 * Lit le bloc `couleursMetier` d'un JSON stocké. Toute entrée invalide —
 * métier inconnu, teinte hors palette (retirée depuis, ou JSON retouché à la
 * main) — est ignorée : le métier garde alors sa couleur d'origine.
 */
export function lireReglage(brut: unknown): ReglageCouleurs {
  if (!brut || typeof brut !== 'object') return { metiers: {} }
  const metiers: Partial<Record<Metier, ChoixCouleur>> = {}
  for (const m of METIERS_ORDRE) {
    const e = (brut as Record<string, unknown>)[m]
    if (!e || typeof e !== 'object') continue
    const { hex, par, le } = e as Record<string, unknown>
    const teinte = typeof hex === 'string' ? teinteParHex(hex) : undefined
    if (!teinte) continue
    metiers[m] = {
      hex: teinte.hex,
      par: typeof par === 'string' ? par : '',
      le: typeof le === 'string' ? le : '',
    }
  }
  return { metiers }
}

/**
 * Couleur de chaque métier : la couleur choisie, sinon celle d'origine.
 *
 * Garantit l'unicité même sur un stockage incohérent (deux écritures
 * simultanées) : les couleurs choisies passent d'abord, dans l'ordre des
 * métiers ; un doublon retombe sur la couleur d'origine, puis sur la
 * première teinte libre.
 */
export function couleursEffectives(r: ReglageCouleurs): Record<Metier, string> {
  const prises = new Set<string>()
  const resultat: Partial<Record<Metier, string>> = {}

  for (const m of METIERS_ORDRE) {
    const choisie = r.metiers[m]?.hex
    if (choisie && !prises.has(choisie)) {
      resultat[m] = choisie
      prises.add(choisie)
    }
  }
  for (const m of METIERS_ORDRE) {
    if (resultat[m]) continue
    const defaut = COULEURS_METIER_DEFAUT[m]
    const libre = prises.has(defaut)
      ? (PALETTE_METIER.find((t) => !prises.has(t.hex))?.hex ?? defaut)
      : defaut
    resultat[m] = libre
    prises.add(libre)
  }
  return resultat as Record<Metier, string>
}

export type RefusCouleur =
  | { raison: 'hors-palette' }
  | { raison: 'inchangee' }
  | { raison: 'deja-prise'; par: Metier }

/**
 * Change la couleur d'un métier, sur un réglage FRAIS (relu juste avant
 * d'écrire : un autre coordinateur a pu prendre la teinte entre-temps).
 */
export function changerCouleur(
  r: ReglageCouleurs,
  metier: Metier,
  hex: string,
  par: string,
  le: string,
): { ok: true; reglage: ReglageCouleurs } | { ok: false; refus: RefusCouleur } {
  const teinte = teinteParHex(hex)
  if (!teinte) return { ok: false, refus: { raison: 'hors-palette' } }

  const actuelles = couleursEffectives(r)
  if (actuelles[metier] === teinte.hex) return { ok: false, refus: { raison: 'inchangee' } }
  const occupant = METIERS_ORDRE.find((m) => m !== metier && actuelles[m] === teinte.hex)
  if (occupant) return { ok: false, refus: { raison: 'deja-prise', par: occupant } }

  return {
    ok: true,
    reglage: { metiers: { ...r.metiers, [metier]: { hex: teinte.hex, par, le } } },
  }
}

/** Message lisible d'un refus. */
export function messageRefus(refus: RefusCouleur): string {
  switch (refus.raison) {
    case 'hors-palette':
      return 'Cette couleur ne fait pas partie de la palette Sartorius.'
    case 'inchangee':
      return 'C\'est déjà la couleur de ce métier.'
    case 'deja-prise':
      return `Cette couleur vient d'être prise par ${refus.par}. Choisissez-en une autre.`
    default: {
      const inconnu: never = refus
      return String(inconnu)
    }
  }
}

/** Le coordinateur du métier, ou l'administrateur (décision 11-A). */
export function peutChangerCouleur(upn: string, metier: Metier): boolean {
  return estAdmin(upn) || getMetiersCoordonnes(upn).includes(metier)
}

/** Nom CSS de la variable d'un métier (`--color-metier-meca`). */
export function variableMetier(metier: Metier): string {
  return `--color-metier-${metier.toLowerCase()}`
}

/**
 * Les variables CSS à poser sur `:root` : la teinte de chaque métier, et la
 * couleur du texte qui se lit dessus.
 */
export function variablesCss(couleurs: Record<Metier, string>): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const m of METIERS_ORDRE) {
    vars[variableMetier(m)] = couleurs[m]
    vars[`${variableMetier(m)}-texte`] = texteSur(couleurs[m])
  }
  return vars
}
